import { z } from 'zod';
import { DIETS, MEAL_TYPES, RECIPE_LANGUAGES, SOURCE_TYPES } from '../recipe.ts';
import { catalogIngredientSchema } from './catalog.ts';
import { unitSchema } from './units.ts';

export { unitSchema } from './units.ts';

const text = (max: number) => z.string().trim().max(max);
const minutes = z.number().int().min(0).max(10_000).nullable();

export const recipeStepSchema = z.object({
  text: text(4000).min(1),
  timerSec: z
    .number()
    .int()
    .positive()
    .max(48 * 3600)
    .optional(),
  /** The component the step works on ("Sauce"): matches an ingredient group label. */
  group: text(80).min(1).optional(),
});
export type RecipeStepInput = z.infer<typeof recipeStepSchema>;

/** One ingredient line as sent by the editor or an importer. */
export const ingredientLineInputSchema = z.object({
  /** Keeps a line's identity across edits; new lines leave it out. */
  id: z.uuid().optional(),
  groupLabel: text(80).min(1).nullable().default(null),
  rawText: text(300).min(1),
  quantity: z.number().nonnegative().nullable().default(null),
  quantityMax: z.number().nonnegative().nullable().default(null),
  unit: unitSchema.nullable().default(null),
  name: text(200).min(1),
  ingredientId: z.uuid().nullable().default(null),
  grams: z.number().nonnegative().nullable().default(null),
  note: text(300).min(1).nullable().default(null),
  optional: z.boolean().default(false),
  /** 0–1, how sure the catalog match is (importers; 1 for a human's pick). */
  matchConfidence: z.number().min(0).max(1).nullable().default(null),
});
export type IngredientLineInput = z.input<typeof ingredientLineInputSchema>;

/** Create or fully replace a recipe (the editor, importers). */
export const recipeInputSchema = z.object({
  /** Client-generated UUIDv7 (optional). */
  id: z.uuid().optional(),
  title: text(200).min(1),
  description: text(2000).nullable().default(null),
  yieldPortions: z.number().int().min(1).max(100),
  prepMin: minutes.default(null),
  cookMin: minutes.default(null),
  totalMin: minutes.default(null),
  steps: z.array(recipeStepSchema).max(100).default([]),
  /** Work to do hours ahead ("Kichererbsen über Nacht einweichen"): the evening-before push. */
  prepAhead: z.array(text(300).min(1)).max(10).default([]),
  sourceType: z.enum(SOURCE_TYPES).default('manual'),
  sourceUrl: z
    .url({ protocol: /^https?$/ })
    .max(2000)
    .nullable()
    .default(null),
  sourceAuthor: text(200).nullable().default(null),
  imageId: z.uuid().nullable().default(null),
  tags: z.array(text(40).min(1)).max(30).default([]),
  mealTypes: z.array(z.enum(MEAL_TYPES)).default([]),
  cuisine: text(60).nullable().default(null),
  diets: z.array(z.enum(DIETS)).default([]),
  mealPrepFriendly: z.boolean().default(false),
  freezerFriendly: z.boolean().default(false),
  fridgeDays: z.number().int().min(0).max(60).nullable().default(null),
  language: z.enum(RECIPE_LANGUAGES).default('de'),
  ingredients: z.array(ingredientLineInputSchema).max(150).default([]),
});
export type RecipeInput = z.input<typeof recipeInputSchema>;

/** Small edits from the detail screen (no ingredient changes). */
export const recipePatchSchema = z
  .object({
    favourite: z.boolean(),
    rating: z.number().int().min(1).max(5).nullable(),
    privateNotes: text(4000).nullable(),
    /** Marks a new import as reviewed (true) or unreviewed again (false). */
    reviewed: z.boolean(),
  })
  .partial();
export type RecipePatch = z.infer<typeof recipePatchSchema>;

/** Several recipes at once from the list's select mode: mark them reviewed or delete them. */
export const recipeBulkSchema = z.object({
  action: z.enum(['review', 'delete']),
  ids: z.array(z.uuid()).min(1).max(500),
});
export type RecipeBulk = z.infer<typeof recipeBulkSchema>;

/**
 * A recipe in its second language (ADR 0010): written by the translation job, never by the
 * editor. Steps line up with the recipe's steps by index; `groups` maps each ingredient group
 * label to its translation.
 */
export const recipeTranslationSchema = z.object({
  language: z.enum(RECIPE_LANGUAGES),
  title: z.string(),
  description: z.string().nullable(),
  steps: z.array(z.string()),
  /** Lines up with the recipe's prep-ahead; missing in translations from before P4.01. */
  prepAhead: z.array(z.string()).optional(),
  tags: z.array(z.string()),
  cuisine: z.string().nullable(),
  groups: z.record(z.string(), z.string()),
});
export type RecipeTranslation = z.infer<typeof recipeTranslationSchema>;

/** An ingredient line's name and note in the recipe's second language. */
export const lineTranslationSchema = z.object({
  name: z.string(),
  note: z.string().nullable(),
});
export type LineTranslation = z.infer<typeof lineTranslationSchema>;

export const ingredientLineSchema = ingredientLineInputSchema.extend({
  id: z.uuid(),
  position: z.number().int(),
  /** Null until translated (and again after the line's text changed). */
  translation: lineTranslationSchema.nullable(),
});
export type IngredientLine = z.infer<typeof ingredientLineSchema>;

export const recipeSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  description: z.string().nullable(),
  yieldPortions: z.number().int(),
  prepMin: z.number().int().nullable(),
  cookMin: z.number().int().nullable(),
  totalMin: z.number().int().nullable(),
  steps: z.array(recipeStepSchema),
  prepAhead: z.array(z.string()),
  sourceType: z.enum(SOURCE_TYPES),
  sourceUrl: z.string().nullable(),
  sourceAuthor: z.string().nullable(),
  imageId: z.uuid().nullable(),
  tags: z.array(z.string()),
  mealTypes: z.array(z.enum(MEAL_TYPES)),
  cuisine: z.string().nullable(),
  diets: z.array(z.enum(DIETS)),
  mealPrepFriendly: z.boolean(),
  freezerFriendly: z.boolean(),
  fridgeDays: z.number().int().nullable(),
  rating: z.number().int().nullable(),
  favourite: z.boolean(),
  privateNotes: z.string().nullable(),
  reviewedAt: z.iso.datetime().nullable(),
  cookedCount: z.number().int(),
  lastCookedOn: z.iso.date().nullable(),
  language: z.enum(RECIPE_LANGUAGES),
  /** The recipe in the other language; null until the translation job ran (or after an edit). */
  translation: recipeTranslationSchema.nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  ingredients: z.array(ingredientLineSchema),
  /** The catalog entries the lines are matched to (household prices applied). */
  catalog: z.array(catalogIngredientSchema),
});
export type Recipe = z.infer<typeof recipeSchema>;

/** List entry (cards). */
export const recipeSummarySchema = recipeSchema
  .pick({
    id: true,
    title: true,
    yieldPortions: true,
    totalMin: true,
    prepMin: true,
    cookMin: true,
    sourceType: true,
    imageId: true,
    tags: true,
    mealTypes: true,
    diets: true,
    mealPrepFriendly: true,
    freezerFriendly: true,
    rating: true,
    favourite: true,
    reviewedAt: true,
    lastCookedOn: true,
    createdAt: true,
  })
  .extend({
    /** Title and tags in the recipe's other language, for the list in that language. */
    translation: recipeTranslationSchema
      .pick({ language: true, title: true, tags: true })
      .nullable(),
    /** Computed from the catalog; null when nothing could be counted. */
    kcalPerPortion: z.number().nullable(),
    proteinPerPortion: z.number().nullable(),
    costPerPortion: z.number().nullable(),
    /** Nutrition incomplete or partly estimated (≈). */
    nutritionEstimate: z.boolean(),
  });
export type RecipeSummary = z.infer<typeof recipeSummarySchema>;

export const recipeListSchema = z.object({ recipes: z.array(recipeSummarySchema) });

/** Fixing one line from the detail screen: its catalog match and/or weight. */
export const ingredientLinePatchSchema = z
  .object({
    ingredientId: z.uuid().nullable(),
    grams: z.number().positive().max(100_000).nullable(),
  })
  .partial();
export type IngredientLinePatch = z.infer<typeof ingredientLinePatchSchema>;
